import React, { useState } from 'react';
import { X, AlertTriangle, Loader2, CheckCircle2 } from 'lucide-react';
import supabase from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';

export default function EscalationModal({ isOpen, onClose, leadId, leadName, onSuccess }) {
  const { user } = useAuth();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const closeAndReset = () => {
    setSubmitted(false);
    setReason('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!reason.trim()) return;
    setSubmitting(true);
    setError(null);

    const { error: insertError } = await supabase.from('escalations').insert({
      tenant_id: user.tenant_id,
      lead_id: leadId,
      escalated_by: user.id,
      reason: reason.trim(),
    });

    setSubmitting(false);

    if (insertError) {
      if (insertError.message?.includes('escalations_one_open_per_lead')) {
        setError('This lead already has an open escalation.');
      } else {
        setError('Failed to submit escalation. Please try again.');
      }
      return;
    }

    setSubmitted(true);
    onSuccess?.();
    setTimeout(closeAndReset, 1500);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-[9990]" onClick={submitted ? undefined : onClose} />
      <div className="fixed inset-0 z-[9991] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden">
          {submitted ? (
            <div className="px-6 py-10 flex flex-col items-center text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-3">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-base font-semibold text-gray-900">Submitted</h2>
              <p className="text-sm text-gray-500 mt-1">Thanks, the SurFox team will take a look.</p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">Escalate Lead</h2>
                    <p className="text-xs text-gray-500">{leadName}</p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Body */}
              <div className="px-5 py-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What's the issue?
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Describe the issue with the AI conversation, any adjustments needed, etc."
                  rows={4}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 resize-none"
                  autoFocus
                />
                {error && (
                  <p className="mt-2 text-xs text-red-600">{error}</p>
                )}
              </div>

              {/* Footer */}
              <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!reason.trim() || submitting}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Escalate
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
